/**
 * DEC-154 MIDI Learn block — arm, watch OPBridge's non-destructive inbound
 * traffic monitor on ANY port, capture, save. Lives inline in the Inspector next to
 * an item's other settings for per-item bindings (sub-decision 3 — no
 * separate global bindings screen for THOSE), reused as-is in Settings for
 * the one binding that has no item to live on: Panic (`itemId="panic"`,
 * `layout/projectStore.ts`'s `PANIC_TRIGGER_ID`). Reusable across item
 * kinds as A-path gets generalized — callers pass `path` fixed per kind.
 */

import { createSignal, Show, onCleanup } from 'solid-js';
import { primaryHttpClient } from '../opbridge/store.ts';
import { midiTriggerConflict, type MidiTriggerBinding } from '../layout/projectStore.ts';
import { pollForMidiTrigger, RECOMMENDED_TRIGGER_PORT } from '../midiTrigger/learn.ts';
import { pushToast } from '../ui/toast.ts';

function describeBinding(b: MidiTriggerBinding): string {
  const kind = b.match.type === 'note' ? 'Note' : 'CC';
  return `${kind} ${b.match.number} · Ch ${b.match.channel + 1} · ${b.port}`;
}

export function MidiTriggerLearn(props: {
  itemId: string;
  binding: MidiTriggerBinding | undefined;
  path: 'a' | 'b';
  onSave: (binding: MidiTriggerBinding) => void;
  onClear: () => void;
  /** `kit`: drawn as a Navigate-kit row (DEC-255), for Settings. The default
   *  is the Inspector's own look, where every other field sits. */
  variant?: 'inspector' | 'kit';
}) {
  const [arming, setArming] = createSignal(false);
  let controller: AbortController | null = null;

  onCleanup(() => controller?.abort());

  async function learn(): Promise<void> {
    const client = primaryHttpClient();
    if (!client) {
      pushToast('warn', 'MIDI Learn needs a connected bridge.');
      return;
    }
    controller?.abort();
    controller = new AbortController();
    setArming(true);
    try {
      // Reads OPBridge's NON-destructive traffic monitor, so this no longer
      // races the background inbound-MIDI poll (which drains `/midi/recent`).
      // The `suspendMidiInPoll` coordination that used to be needed here is
      // deleted - see `midiTrigger/learn.ts` for why the monitor is the right
      // surface for a desk-time flow and the wrong one for anything on stage.
      const captured = await pollForMidiTrigger(client, { signal: controller.signal });
      if (!captured) {
        if (!controller.signal.aborted) {
          pushToast('warn', `No MIDI seen on any port - press the control again, or route it to ${RECOMMENDED_TRIGGER_PORT}.`);
        }
        return;
      }
      const conflict = midiTriggerConflict(captured.port, captured.match, props.itemId);
      if (conflict) {
        pushToast('error', `That MIDI event is already bound to "${conflict.label}".`);
        return;
      }
      props.onSave({ ...captured, path: props.path });
      pushToast('ok', 'MIDI trigger learned.');
    } finally {
      setArming(false);
    }
  }

  function cancel(): void {
    controller?.abort();
    setArming(false);
  }

  // Settings is a Navigate section, so it gets the kit (2026-10-07, operator:
  // "update that" - the old mono capitals and cyan button broke DEC-255).
  if (props.variant === 'kit') {
    return (
      <div class="nav-lr midi-trigger-learn" data-variant="kit">
        <span class="nav-lr-main">
          <b>{props.binding ? describeBinding(props.binding) : 'No pedal yet'}</b>
          <small>
            {arming()
              ? 'Listening: press the pedal or pad now.'
              : props.binding
                ? (props.path === 'a' ? 'Fires even with this tab closed.' : 'Needs OPButtons open.')
                : 'Press MIDI Learn, then press the pedal or pad.'}
          </small>
        </span>
        <span class="nav-lr-trail">
          <Show
            when={!arming()}
            fallback={<button type="button" class="nv-b nv-b-sm midi-trigger-arming" onClick={cancel}>Cancel</button>}
          >
            <button type="button" class="nv-b nv-b-sm" onClick={() => void learn()}>{props.binding ? 'Learn again' : 'MIDI Learn'}</button>
          </Show>
          <Show when={props.binding}>
            <button type="button" class="nv-b nv-b-q nv-b-sm" onClick={props.onClear}>Clear</button>
          </Show>
        </span>
      </div>
    );
  }

  return (
    <div class="inspector-zone midi-trigger-learn">
      <div class="inspector-field inspector-field-grow">
        <span>MIDI trigger</span>
        <Show
          when={props.binding}
          fallback={<span class="midi-trigger-empty">Not bound</span>}
        >
          {(b) => (
            <span class="midi-trigger-bound">
              {describeBinding(b())}
              <span class={`midi-trigger-badge midi-trigger-badge-${props.path}`} title={
                props.path === 'a'
                  ? 'Fires even with the tab closed'
                  : 'Needs the OPButtons tab open'
              }>
                {props.path.toUpperCase()}
              </span>
            </span>
          )}
        </Show>
      </div>
      <Show
        when={!arming()}
        fallback={
          <button type="button" class="inspector-secondary midi-trigger-arming" onClick={cancel}>
            Listening… tap to cancel
          </button>
        }
      >
        <button type="button" class="inspector-secondary" onClick={() => void learn()}>
          {props.binding ? 'Re-learn…' : 'MIDI Learn…'}
        </button>
      </Show>
      <Show when={props.binding}>
        <button type="button" class="inspector-secondary" onClick={props.onClear}>
          Clear
        </button>
      </Show>
    </div>
  );
}
